import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Check,
  Copy,
  ExternalLink,
  Eye,
  Inbox,
  MousePointerClick,
  Pencil,
  Send,
  TriangleAlert
} from 'lucide-react';
import CommonHeader from '../../util_components/common_header';
import NoPermission from '../../util_components/no_permission';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import { EventDetails } from '../students/student_profile/StudentActivity';
import { api_error_message as apiErrorMessage } from '../../../utils/api_error_message';
import {
  EVENT_NAMES,
  PAGE_SIZE,
  eventLabel,
  buildQuery,
  dateLimits,
  defaultDates,
  describeParts,
  formatClock,
  formatFull,
  groupByIstDay,
  humanize_event_name as humanizeEventName,
  initials,
  rangeLabel
} from './cms_activity_helpers';

// Every <button> here sets m-0 and its own background, border and padding: the global `button {}`
// rule (common.css) adds a 5px margin, the brand fill and 10px 20px padding to every button.
// One ring for every control, the same outline the inputs use. The global `button {}` rule does
// not set one, so without it a keyboard user gets only the UA default, which the transparent
// buttons and the sunken filter panel hide.
const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--input-focus-border)]';
const INPUT = `h-9 w-full rounded-md border border-[var(--input-border)] bg-[var(--input-bg)] px-2.5 text-sm normal-case tracking-normal text-[var(--input-text)] placeholder:text-[var(--input-placeholder)] ${FOCUS}`;
const LABEL =
  'flex min-w-0 flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-content-secondary';
// Apply sits on --action-dark-bg, not --brand-primary: white on the brand orange is 3.0:1, under AA.
const PRIMARY = `m-0 h-9 flex-1 rounded-md border-0 bg-[var(--action-dark-bg)] px-4 text-sm font-semibold text-[var(--text-on-brand)] hover:bg-[var(--action-dark-bg-hover)] lg:flex-none ${FOCUS}`;
const SECONDARY_SM = `m-0 h-8 rounded-md border border-line bg-surface-card px-3 text-[13px] font-semibold text-content-primary disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`;
// No element here carries both `flex` and `items-center`. common.css:819 turns every
// `.common-page-container .flex.items-center` under 769px into a full-width, stretched column
// (it made each 24px avatar a 197px bar and pushed the Inactive badge out of the card), so rows
// centre with the arbitrary [align-items:center] and single-child boxes use inline-flex, which the
// selector does not match. Same escape as reports/concierge_v3.css, which avoids the pair too.
const ROW_FLEX = 'flex [align-items:center]';
const LINK_BUTTON = `m-0 h-9 border-0 bg-transparent px-1 text-[13px] font-medium text-content-secondary underline underline-offset-[3px] hover:text-content-primary ${FOCUS}`;

// A grid list, not a <table>: common.css centres every th and td globally, and a log is read down
// its left edge. The areas stack into four lines under md (time and event, user, what, entity) and
// sit in one row from md up. With one user filtered, every row would repeat that user, so the
// column goes and the name moves to the result bar.
const ROW_BASE =
  'grid grid-cols-[auto_1fr] gap-y-1.5 border-b border-line px-4 py-2.5 text-sm last:border-b-0 hover:bg-surface-sunken gap-x-3 md:items-center md:gap-x-4';
const ROW_WITH_USER = `${ROW_BASE} [grid-template-areas:'t_ev'_'who_who'_'what_what'_'ent_ent'] md:grid-cols-[72px_180px_128px_minmax(0,1fr)_auto] md:[grid-template-areas:'t_who_ev_what_ent']`;
const ROW_ONE_USER = `${ROW_BASE} [grid-template-areas:'t_ev'_'what_what'_'ent_ent'] md:grid-cols-[72px_128px_minmax(0,1fr)_auto] md:[grid-template-areas:'t_ev_what_ent']`;

// Two more template rows for a change row's Details line, which spans the whole row; the view
// templates above stay as they are so the rows that outnumber changes are untouched.
const ROW_WITH_USER_DETAILS = `${ROW_BASE} [grid-template-areas:'t_ev'_'who_who'_'what_what'_'ent_ent'_'det_det'] md:grid-cols-[72px_180px_128px_minmax(0,1fr)_auto] md:[grid-template-areas:'t_who_ev_what_ent'_'det_det_det_det_det']`;
const ROW_ONE_USER_DETAILS = `${ROW_BASE} [grid-template-areas:'t_ev'_'what_what'_'ent_ent'_'det_det'] md:grid-cols-[72px_128px_minmax(0,1fr)_auto] md:[grid-template-areas:'t_ev_what_ent'_'det_det_det_det']`;

const CHANGE_CHIP = 'bg-[var(--activity-change-bg)] text-[var(--activity-change-text)]';

// Views and changes are different reads on goserver (kind), so the control applies on click rather
// than waiting for Apply: the Event and Module filters below only exist for views.
const KINDS = [
  { value: 'change', label: 'Changes' },
  { value: 'view', label: 'Views & interactions' },
  { value: 'all', label: 'All' }
];

const EVENT_STYLE = {
  page_view: { icon: Eye, chip: 'bg-[var(--activity-view-bg)] text-[var(--activity-view-text)]' },
  interaction: {
    icon: MousePointerClick,
    chip: 'bg-[var(--activity-interaction-bg)] text-[var(--activity-interaction-text)]'
  },
  form_submit: {
    icon: Send,
    chip: 'bg-[var(--activity-submit-bg)] text-[var(--activity-submit-text)]'
  }
};

// Entity types with a profile page to open, same URLs as Home's teacher and student links.
const PROFILE_PATHS = { student: '/studentprofile', teacher: '/teacherprofile' };

const emptyFilters = () => ({
  ...defaultDates(),
  kind: 'change',
  cmsUserUuid: '',
  entityUuid: '',
  module: '',
  eventName: ''
});
const isDefault = (filters) => JSON.stringify(filters) === JSON.stringify(emptyFilters());

// Who did it, by name. The reader returns only cms_user_uuid; /activity/users is gated by the
// same allowlist and includes deactivated users, whose past activity is exactly what an audit
// looks for. If it fails anyway, rows show short uuids and the filter takes a uuid.
const loadUserNames = () =>
  getApi(getApiUrl('/v2/cms_user/activity/users'), true)
    .then((res) =>
      (res.data.data.users || []).reduce(
        (names, user) => ({
          ...names,
          [user.uuid]: {
            name: user.name || user.email || user.uuid,
            inactive: user.is_active === 0
          }
        }),
        {}
      )
    )
    .catch(() => null);

// Copies the full uuid; the chip shows only its first 8 characters. The tick reverts after 1.5 s,
// and the live region tells a screen reader the copy happened, since the icon swap is silent.
const COPY_MESSAGE = { copied: 'Copied', failed: 'Copy failed' };
function CopyUuid({ uuid }) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!result) return undefined;
    const timer = setTimeout(() => setResult(null), 1500);
    return () => clearTimeout(timer);
  }, [result]);
  // writeText rejects when the document is unfocused or the permission is denied. The tick stays
  // off, so it never claims a copy that did not happen, and the live region says so instead.
  const copy = () =>
    navigator.clipboard.writeText(uuid).then(
      () => setResult('copied'),
      () => setResult('failed')
    );
  const Icon = result === 'copied' ? Check : Copy;
  return (
    <>
      <button
        type="button"
        aria-label="Copy uuid"
        data-track="cms-activity-copy-uuid"
        className={`m-0 inline-flex h-6 w-6 items-center justify-center rounded-md border-0 bg-transparent p-0 text-content-secondary hover:bg-surface-sunken hover:text-content-primary max-md:h-8 max-md:w-8 ${FOCUS}`}
        onClick={copy}
      >
        <Icon size={14} aria-hidden="true" />
      </button>
      <span className="sr-only" aria-live="polite">
        {COPY_MESSAGE[result] || ''}
      </span>
    </>
  );
}
CopyUuid.propTypes = { uuid: PropTypes.string.isRequired };

function EntityChip({ type, uuid }) {
  const kind = type || 'entity';
  const label = kind.charAt(0).toUpperCase() + kind.slice(1);
  const path = PROFILE_PATHS[kind];
  const chip = `inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border border-line bg-surface-sunken px-2 text-xs text-content-secondary no-underline hover:border-[var(--border-emphasis)] max-md:h-8 ${FOCUS}`;
  const body = (
    <>
      {label}
      <code className="font-mono text-[var(--state-info)]">{uuid.slice(0, 8)}</code>
      {path ? <ExternalLink size={11} aria-hidden="true" /> : null}
    </>
  );
  return (
    <span className="inline-flex items-center gap-0.5 justify-self-start [grid-area:ent] md:justify-self-end">
      {path ? (
        <a
          href={`${path}?id=${encodeURIComponent(uuid)}`}
          target="_blank"
          rel="noreferrer"
          title={uuid}
          aria-label={`Open ${kind} ${uuid}`}
          data-track="cms-activity-open-entity"
          className={chip}
        >
          {body}
        </a>
      ) : (
        <span title={uuid} className={chip}>
          {body}
        </span>
      )}
      <CopyUuid uuid={uuid} />
    </span>
  );
}
EntityChip.propTypes = { type: PropTypes.string, uuid: PropTypes.string.isRequired };
EntityChip.defaultProps = { type: null };

function UserCell({ uuid, user }) {
  const name = user ? user.name : `${uuid.slice(0, 8)}…`;
  return (
    <span className={`${ROW_FLEX} min-w-0 gap-2 [grid-area:who]`}>
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border border-line bg-surface-sunken text-[10px] font-bold text-content-secondary"
      >
        {initials(user && user.name, uuid)}
      </span>
      <span
        title={user ? user.name : uuid}
        className={`min-w-0 truncate ${user ? '' : 'font-mono text-xs'} ${
          user && user.inactive ? 'text-content-secondary' : 'text-content-primary'
        }`}
      >
        {name}
      </span>
      {user && user.inactive ? (
        <span className="flex-none rounded-full bg-[var(--status-closed-bg)] px-1.5 py-px text-[11px] font-medium text-[var(--status-closed-text)]">
          Inactive
        </span>
      ) : null}
    </span>
  );
}
UserCell.propTypes = {
  uuid: PropTypes.string.isRequired,
  user: PropTypes.shape({ name: PropTypes.string, inactive: PropTypes.bool })
};
UserCell.defaultProps = { user: null };

function ActivityRow({ row, user, showUser }) {
  const [open, setOpen] = useState(false);
  const isChange = row.kind === 'change';
  const style = isChange
    ? { icon: Pencil, chip: CHANGE_CHIP }
    : EVENT_STYLE[row.event_name] || EVENT_STYLE.interaction;
  const Icon = style.icon;
  const { label, route } = isChange
    ? { label: humanizeEventName(row.event_name), route: row.change.summary }
    : describeParts(row);
  const detailsId = `cms-activity-change-${row.event_uuid}`;
  let rowClass = showUser ? ROW_WITH_USER : ROW_ONE_USER;
  if (isChange) rowClass = showUser ? ROW_WITH_USER_DETAILS : ROW_ONE_USER_DETAILS;
  return (
    <li className={rowClass}>
      <time
        dateTime={new Date(row.occurred_at).toISOString()}
        title={formatFull(row.occurred_at)}
        className="whitespace-nowrap text-[13px] tabular-nums text-content-secondary [grid-area:t]"
      >
        {formatClock(row.occurred_at)}
      </time>
      {showUser ? <UserCell uuid={row.cms_user_uuid} user={user} /> : null}
      <span
        className={`inline-flex items-center gap-1.5 justify-self-start whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold [grid-area:ev] ${style.chip}`}
      >
        <Icon size={13} aria-hidden="true" />
        {isChange ? 'Change' : eventLabel(row.event_name)}
      </span>
      {/* A change's action name is the one thing the row exists to say, and with a summary beside
          it the nowrap flex squeezed it to "Create Car…" at 1440. Changes wrap instead, so the
          full name always shows and the summary drops below it; views keep the single line. */}
      <span
        className={`flex min-w-0 flex-wrap items-baseline gap-x-2 [grid-area:what] ${
          isChange ? '' : 'md:flex-nowrap'
        }`}
      >
        <span
          className={`font-semibold text-content-primary ${isChange ? 'break-words' : 'md:truncate'}`}
        >
          {label}
        </span>
        <span
          className={`truncate text-xs text-content-secondary ${isChange ? '' : 'font-mono'}`}
          title={isChange ? route : undefined}
        >
          {route}
        </span>
      </span>
      {row.entity_uuid ? <EntityChip type={row.entity_type} uuid={row.entity_uuid} /> : null}
      {isChange ? (
        <div className="min-w-0 [grid-area:det]">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={detailsId}
            data-track="cms-activity-change-details"
            className={`m-0 h-8 rounded-md border-0 bg-transparent px-1 text-[13px] font-medium text-content-secondary underline underline-offset-[3px] hover:text-content-primary ${FOCUS}`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide details' : 'Details'}
            <span className="sr-only">{` for ${label}`}</span>
          </button>
          {/* EventDetails is light only: white cards with no text colour of its own, so in dark it
              inherited light text onto white and every cell read blank. The panel stays an explicit
              light surface, as the Student tab's modal does, rather than half themed. */}
          {open ? (
            <div
              id={detailsId}
              className="mt-1 overflow-hidden rounded-lg border border-line bg-white text-[#172033] [&_td]:!text-[#172033] [&_th]:!text-[#172033]"
            >
              <EventDetails event={row.change} is_cms_action />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
ActivityRow.propTypes = {
  row: PropTypes.shape({
    event_uuid: PropTypes.string,
    kind: PropTypes.string,
    change: PropTypes.shape({ summary: PropTypes.string }),
    event_name: PropTypes.string,
    occurred_at: PropTypes.number,
    cms_user_uuid: PropTypes.string,
    route: PropTypes.string,
    entity_type: PropTypes.string,
    entity_uuid: PropTypes.string
  }).isRequired,
  user: PropTypes.shape({ name: PropTypes.string, inactive: PropTypes.bool }),
  showUser: PropTypes.bool.isRequired
};
ActivityRow.defaultProps = { user: null };

// Bars on --border-default, not --surface-sunken: in dark the sunken surface is barely distinct
// from the card, so the skeleton would read as an empty list.
const BAR = 'h-3 rounded bg-[var(--border-default)] animate-pulse motion-reduce:animate-none';
function SkeletonRows({ showUser }) {
  return (
    <>
      <div className="border-b border-line bg-surface-sunken px-4 py-2.5">
        <div className="h-3 w-36 rounded bg-[var(--border-default)]" />
      </div>
      <ol className="m-0 list-none p-0">
        {Array.from({ length: 8 }, (_, i) => (
          <li key={i} className={showUser ? ROW_WITH_USER : ROW_ONE_USER}>
            <span className={`${BAR} w-12 [grid-area:t]`} />
            {showUser ? <span className={`${BAR} w-[70%] [grid-area:who]`} /> : null}
            <span className={`${BAR} h-[18px] w-20 rounded-full [grid-area:ev]`} />
            <span className={`${BAR} w-[55%] [grid-area:what]`} />
            <span className={`${BAR} w-[120px] [grid-area:ent]`} />
          </li>
        ))}
      </ol>
    </>
  );
}
SkeletonRows.propTypes = { showUser: PropTypes.bool.isRequired };

function CmsActivity() {
  const [form, setForm] = useState(emptyFilters);
  const [applied, setApplied] = useState(form);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  // loading | ready | error | forbidden. forbidden is the reader's 403: only the two allowlisted
  // emails may read this, and anyone else who reaches the URL sees why instead of an empty list.
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [users, setUsers] = useState(null);
  // A slow response for an older filter or page must not overwrite the one asked for last.
  const latest = useRef(0);
  const listRef = useRef(null);

  // Only filters that passed buildQuery in apply() reach here, so built.query is always set.
  const load = useCallback((filters, nextPage) => {
    const built = buildQuery(filters, nextPage);
    latest.current += 1;
    const id = latest.current;
    setStatus('loading');
    getApi(getApiUrl(`/v2/cms_user/activity?${built.query}`), true)
      .then((res) => {
        if (id !== latest.current) return;
        setRows(res.data.data.events || []);
        setHasMore(Boolean(res.data.data.pagination && res.data.data.pagination.has_more));
        setStatus('ready');
      })
      .catch((e) => {
        if (id !== latest.current) return;
        const code = e.response && e.response.status;
        setRows([]);
        setHasMore(false);
        setStatus(code === 403 ? 'forbidden' : 'error');
        // 502/504: ClickHouse failed or passed the reader's 15s budget; a narrower window is the
        // lever the reader has.
        setMessage(
          code === 502 || code === 504
            ? 'The activity store did not answer in time. Try a shorter date range.'
            : apiErrorMessage(e, 'Could not load CMS activity. Please try again.')
        );
      });
  }, []);

  useEffect(() => {
    load(applied, page);
  }, [load, applied, page]);

  useEffect(() => {
    loadUserNames().then(setUsers);
  }, []);

  const isChangeMode = form.kind === 'change';

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // Every filter change starts again at page 1: page 3 of one filter is not page 3 of another.
  // An invalid filter is refused at the form, so the last good results stay on screen.
  const apply = (e) => {
    e.preventDefault();
    const { error } = buildQuery(form, 1);
    setFormError(error || '');
    if (error) return;
    setPage(1);
    setApplied({ ...form });
  };

  const pickKind = (kind) => {
    const next = { ...form, kind };
    const { error } = buildQuery(next, 1);
    setFormError(error || '');
    if (error) return;
    setForm(next);
    setPage(1);
    setApplied({ ...next });
  };

  const reset = () => {
    const fresh = emptyFilters();
    setForm(fresh);
    setFormError('');
    setPage(1);
    setApplied(fresh);
  };

  // Next leaves the reader at the bottom of the new page without this; the list card's top is
  // where the new page starts.
  const goToPage = (next) => {
    setPage(next);
    listRef.current.scrollIntoView({ block: 'start' });
  };

  const limits = dateLimits(form.fromDate, form.toDate);
  const oneUser = applied.cmsUserUuid.trim();
  const userName = (uuid) => (users && users[uuid] ? users[uuid].name : uuid.slice(0, 8));
  const range = rangeLabel(applied.fromDate, applied.toDate);
  const start = (page - 1) * PAGE_SIZE + 1;
  // What the empty state says was filtered, so "nothing for these filters" is not read as
  // "nothing at all".
  const activeFilters = [
    oneUser ? userName(oneUser) : '',
    applied.kind !== 'change' && applied.eventName ? eventLabel(applied.eventName) : '',
    applied.kind !== 'change' && applied.module.trim() ? `module ${applied.module.trim()}` : '',
    applied.entityUuid.trim() ? `id ${applied.entityUuid.trim().slice(0, 8)}` : ''
  ].filter(Boolean);

  if (status === 'forbidden') return <NoPermission />;

  return (
    <CommonHeader
      title="CMS Activity"
      subtitle="What each CMS user did, and when"
      class_name="!overflow-visible"
    >
      {/* The day headers stick to .content, the app's real scroller. .common-page-container sets
          overflow: auto, which would make it their containing block, and since it never scrolls
          itself they would never stick; !overflow-visible above removes that. The form's own grid
          and justify-items-stretch override the unscoped global `form` rule
          (chat_groups/Components/ImageUploader/index.css) that sets column direction and centring
          on every form in the app. The one-row filter needs about 1,150px inside the page card,
          which the drawer and card margins leave only from a window of about 1,600px (measured:
          the card is 981px wide at 1440 and the row spilled out of both sides), so below that lg
          uses two rows of four. */}
      <form
        className={`m-0 mb-4 grid grid-cols-1 items-end justify-items-stretch gap-3 rounded-lg border border-line bg-surface-sunken p-3 sm:grid-cols-2 lg:grid-cols-4 ${
          isChangeMode
            ? 'min-[1600px]:grid-cols-[148px_148px_minmax(170px,1fr)_minmax(200px,1.3fr)_auto]'
            : 'min-[1600px]:grid-cols-[148px_148px_minmax(170px,1fr)_150px_minmax(140px,1fr)_minmax(200px,1.3fr)_auto]'
        }`}
        onSubmit={apply}
        data-track="cms-activity-filters"
      >
        <div
          role="group"
          aria-label="Activity type"
          className="col-span-full inline-flex gap-1 justify-self-start rounded-md border border-line bg-surface-card p-0.5"
        >
          {KINDS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              // hover: classes (specificity 0,2,0) because common.css `button:hover` (0,1,1) would
              // otherwise repaint the selected fill white and its text orange.
              aria-pressed={form.kind === value}
              data-track={`cms-activity-kind-${value}`}
              className={`m-0 h-8 rounded border-0 px-3 text-[13px] font-semibold ${FOCUS} ${
                form.kind === value
                  ? 'bg-[var(--action-dark-bg)] text-[var(--text-on-brand)] hover:bg-[var(--action-dark-bg-hover)] hover:text-[var(--text-on-brand)]'
                  : 'bg-transparent text-content-secondary hover:bg-surface-sunken hover:text-content-primary'
              }`}
              onClick={() => pickKind(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <label className={LABEL} htmlFor="cms-activity-from">
          From
          <input
            id="cms-activity-from"
            type="date"
            className={INPUT}
            value={form.fromDate}
            min={limits.from.min}
            max={limits.from.max}
            onChange={set('fromDate')}
          />
        </label>
        <label className={LABEL} htmlFor="cms-activity-to">
          To
          <input
            id="cms-activity-to"
            type="date"
            className={INPUT}
            value={form.toDate}
            min={limits.to.min}
            max={limits.to.max}
            onChange={set('toDate')}
          />
        </label>
        <label className={`${LABEL} sm:col-span-2 lg:col-span-1`} htmlFor="cms-activity-user">
          CMS user
          {users ? (
            <select
              id="cms-activity-user"
              className={INPUT}
              value={form.cmsUserUuid}
              onChange={set('cmsUserUuid')}
            >
              <option value="">Everyone</option>
              {Object.keys(users)
                .sort((a, b) => users[a].name.localeCompare(users[b].name))
                .map((uuid) => (
                  <option key={uuid} value={uuid}>
                    {users[uuid].inactive ? `${users[uuid].name} (inactive)` : users[uuid].name}
                  </option>
                ))}
            </select>
          ) : (
            <input
              id="cms-activity-user"
              className={INPUT}
              placeholder="CMS user uuid"
              value={form.cmsUserUuid}
              onChange={set('cmsUserUuid')}
            />
          )}
        </label>
        {!isChangeMode ? (
          <>
            <label className={LABEL} htmlFor="cms-activity-event">
              Event
              <select
                id="cms-activity-event"
                className={INPUT}
                value={form.eventName}
                onChange={set('eventName')}
              >
                <option value="">All events</option>
                {EVENT_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {eventLabel(name)}
                  </option>
                ))}
              </select>
            </label>
            <label className={LABEL} htmlFor="cms-activity-module">
              Module
              <input
                id="cms-activity-module"
                className={INPUT}
                placeholder="e.g. studentprofile"
                value={form.module}
                onChange={set('module')}
              />
            </label>
          </>
        ) : null}
        <label className={`${LABEL} sm:col-span-2 lg:col-span-1`} htmlFor="cms-activity-entity">
          Student, teacher or session id
          <input
            id="cms-activity-entity"
            className={INPUT}
            placeholder="Paste a uuid"
            value={form.entityUuid}
            onChange={set('entityUuid')}
          />
        </label>
        <div className={`${ROW_FLEX} gap-2 sm:col-span-2 lg:col-span-1`}>
          <button type="submit" className={PRIMARY}>
            Apply
          </button>
          {!isDefault(form) ? (
            <button
              type="button"
              className={LINK_BUTTON}
              data-track="cms-activity-reset"
              onClick={reset}
            >
              Reset
            </button>
          ) : null}
        </div>
        {formError ? (
          <p className="col-span-full m-0 text-sm text-state-error-text" role="alert">
            {formError}
          </p>
        ) : null}
      </form>

      {status === 'loading' || (status === 'ready' && rows.length) ? (
        <div
          className={`${ROW_FLEX} mb-2 flex-wrap justify-between gap-3 px-0.5 text-[13px] text-content-secondary`}
        >
          {status === 'loading' ? (
            <span>Loading…</span>
          ) : (
            <span>
              <strong className="font-semibold text-content-primary">
                Rows {start}–{start + rows.length - 1}
              </strong>
              {oneUser ? ` · ${userName(oneUser)}` : ''} · {range}
            </span>
          )}
          <span>Times in IST</span>
        </div>
      ) : null}

      <div
        ref={listRef}
        aria-busy={status === 'loading'}
        className="overflow-clip rounded-lg border border-line bg-surface-card"
      >
        {status === 'loading' ? <SkeletonRows showUser={!oneUser} /> : null}

        {status === 'error' ? (
          <div
            className="m-4 flex items-start gap-3 rounded-lg border border-[var(--state-error)] bg-[var(--state-error-bg-subtle)] px-3.5 py-3 text-sm text-[var(--state-error-text)]"
            role="alert"
          >
            <TriangleAlert size={18} aria-hidden="true" className="flex-none" />
            <div>
              <p className="m-0 mb-2">{message}</p>
              <button type="button" className={SECONDARY_SM} onClick={() => load(applied, page)}>
                Retry
              </button>
            </div>
          </div>
        ) : null}

        {status === 'ready' && !rows.length ? (
          <div className="px-4 py-12 text-center text-sm text-content-muted">
            <Inbox size={32} aria-hidden="true" className="mx-auto mb-2" />
            <h4 className="m-0 mb-1 text-sm font-semibold text-content-primary">
              No activity for these filters
            </h4>
            <p className="m-0 mb-3">
              Nothing was recorded between {range.replace(' to ', ' and ')}
              {activeFilters.length ? ` for ${activeFilters.join(', ')}` : ''}.
            </p>
            {!isDefault(applied) ? (
              <button
                type="button"
                className={SECONDARY_SM}
                data-track="cms-activity-reset"
                onClick={reset}
              >
                Reset filters
              </button>
            ) : null}
          </div>
        ) : null}

        {status === 'ready'
          ? groupByIstDay(rows).map((day) => (
              // Not day.key alone: groups are runs of consecutive rows, so a date that recurs
              // (Mon, Tue, Mon) is two groups with one key. Each group's first row is in no other.
              <section
                key={`${day.key}-${day.rows[0].event_uuid}`}
                aria-labelledby={`cms-activity-day-${day.key}-${day.rows[0].event_uuid}`}
              >
                <h3
                  id={`cms-activity-day-${day.key}-${day.rows[0].event_uuid}`}
                  className="sticky top-0 z-[1] m-0 border-b border-line bg-surface-sunken px-4 py-2 text-xs font-semibold uppercase tracking-wide text-content-secondary"
                >
                  {day.label}
                </h3>
                <ol className="m-0 list-none p-0">
                  {day.rows.map((row) => (
                    <ActivityRow
                      key={row.event_uuid}
                      row={row}
                      user={users && users[row.cms_user_uuid]}
                      showUser={!oneUser}
                    />
                  ))}
                </ol>
              </section>
            ))
          : null}

        {status === 'ready' && (page > 1 || hasMore) ? (
          <nav
            className={`${ROW_FLEX} justify-between border-t border-line px-4 py-2.5 text-[13px] text-content-muted`}
            aria-label="CMS activity pages"
          >
            <span>Page {page}</span>
            <span className="flex gap-2">
              <button
                type="button"
                className={SECONDARY_SM}
                disabled={page === 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className={SECONDARY_SM}
                disabled={!hasMore}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </button>
            </span>
          </nav>
        ) : null}

        {status === 'ready' && rows.length && !hasMore ? (
          <p className="m-0 border-t border-line px-4 py-3 text-center text-xs text-content-muted">
            End of activity for these filters
          </p>
        ) : null}
      </div>
    </CommonHeader>
  );
}

export default CmsActivity;
